import { useQuery } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ExpenseExternalPartyDialog,
  type ExpenseExternalParty,
} from '@/components/spending/expense-external-party-dialog';
import { Listbox, type ListboxOption } from '@/components/ui/listbox';
import type { PopoverPortalTarget } from '@/components/ui/popover-portal';
import { api, type Collection } from '@/lib/api';
import { hasPermission, PERMISSIONS } from '@/lib/permissions';
import { useChurchStore } from '@/stores/church-store';

const CREATE_VALUE = '__create_external_party__';

export interface ExpenseExternalPartySelectProps {
  value: string | null;
  onChange: (partyId: string | null, party?: ExpenseExternalParty | null) => void;
  popoverPortal?: PopoverPortalTarget;
  disabled?: boolean;
  id?: string;
  'aria-label'?: string;
}

/**
 * Outside payee picker (companies and similar) with an inline create path.
 */
export const ExpenseExternalPartySelect = ({
  value,
  onChange,
  popoverPortal = 'body',
  disabled = false,
  id,
  'aria-label': ariaLabel,
}: ExpenseExternalPartySelectProps) => {
  const { t } = useTranslation('spending');
  const { t: tTips } = useTranslation('tips');
  const membership = useChurchStore((state) => state.membership);
  const canCreate = hasPermission(membership, PERMISSIONS.EXPENSES_CREATE);

  const [createOpen, setCreateOpen] = useState(false);

  const parties = useQuery({
    queryKey: ['expenses', 'external-parties'],
    queryFn: () =>
      api.get<Collection<ExpenseExternalParty>>('/expenses/external-parties', {
        query: { pageSize: 100 },
      }),
  });

  const options = useMemo((): ListboxOption[] => {
    const rows = (parties.data?.data ?? []).map((row) => ({
      value: row.id,
      label: row.name,
    }));
    if (!canCreate) return rows;
    return [
      ...rows,
      {
        value: CREATE_VALUE,
        label: t('externalParties.createInline'),
        description: tTips('spending.payeeCreateInline'),
        icon: <Plus className="size-4" aria-hidden />,
      },
    ];
  }, [canCreate, parties.data?.data, t, tTips]);

  return (
    <>
      <Listbox
        id={id}
        value={value}
        onChange={(next) => {
          if (next === CREATE_VALUE) {
            setCreateOpen(true);
            return;
          }
          const party = parties.data?.data.find((row) => row.id === next) ?? null;
          onChange(next, party);
        }}
        options={options}
        placeholder={t('externalParties.placeholder')}
        emptyMessage={t('externalParties.emptyPicker')}
        popoverPortal={popoverPortal}
        disabled={disabled || parties.isLoading}
        aria-label={ariaLabel}
      />

      <ExpenseExternalPartyDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onSaved={(party) => onChange(party.id, party)}
      />
    </>
  );
};
